/**
 * Begin Again Guide — Structured navigation flow.
 *
 * Each node has: { title?, prompt, options[] }
 * Each option is one of:
 *  - { label, goto: "<nodeId>" }        → navigate to another guide node
 *  - { label, to: "/path" }             → internal React Router navigation (closes widget)
 *  - { label, href: "https://..." }     → external link (opens new tab, widget stays open)
 *  - { label, action: "restart"|"free-text"|"crisis" }
 *
 * Every non-root node automatically shows a "Return to Main Menu" action
 * rendered by the widget. Every non-crisis node also shows a "Connect with a
 * person" chip row (Contact / Request Support / Prayer / Ask Chaplain) at the
 * bottom of the message.
 */

export const OPENING_MESSAGE =
  "Welcome to Begin Again Living Ministries. I can help you find support, resources, books, classes, prayer options, or ways to connect with the ministry. How can I help today?";

export const HUMAN_CHIPS = [
  { id: "contact", label: "Contact the Ministry", to: "/contact" },
  { id: "request-support", label: "Request Support", to: "/request-support" },
  { id: "prayer", label: "Prayer Request", to: "/request-support?type=prayer" },
  { id: "ask-chaplain", label: "Ask Chaplain Derrick", to: "/ask-chaplain-derrick" },
];

export const FLOW = {
  // -------------------- ROOT --------------------
  main: {
    prompt: OPENING_MESSAGE,
    options: [
      { label: "Grief & Loss", goto: "grief" },
      { label: "Caregiving Support", goto: "caregiving" },
      { label: "Hospice & End-of-Life", goto: "hospice" },
      { label: "Stress & Burnout", goto: "stress" },
      { label: "Prayer Request", goto: "prayer" },
      { label: "Books & Resources", goto: "books" },
      { label: "Workshops & Classes", goto: "workshops" },
      { label: "Ask Chaplain Derrick", goto: "ask" },
      { label: "Contact the Ministry", goto: "contact" },
      { label: "I'm Not Sure Where to Start", goto: "unsure" },
      { label: "Type your concern instead", action: "free-text" },
    ],
  },

  // -------------------- GRIEF & LOSS --------------------
  grief: {
    title: "Grief & Loss",
    prompt: "What would be most helpful right now?",
    options: [
      { label: "I recently experienced a loss", to: "/grief-support" },
      { label: "I'm supporting someone who is grieving", to: "/grief-support" },
      { label: "I need a grief resource", to: "/resources" },
      { label: "I'm looking for a class or workshop", to: "/classes" },
      { label: "I want to talk with someone", to: "/request-support" },
    ],
  },

  // -------------------- CAREGIVING --------------------
  caregiving: {
    title: "Caregiving Support",
    prompt: "What kind of caregiving support are you looking for?",
    options: [
      { label: "Caregiver stress", to: "/resources" },
      { label: "Supporting a loved one", to: "/grief-support" },
      { label: "Communication with family", to: "/from-diagnosis-to-goodbye" },
      { label: "Self-care and burnout", to: "/resources" },
      { label: "Caregiver resources", to: "/resources" },
      { label: "Talk with the ministry", to: "/contact" },
    ],
  },

  // -------------------- HOSPICE --------------------
  hospice: {
    title: "Hospice & End-of-Life",
    prompt:
      "What would you like help finding? (This guide does not provide medical advice.)",
    options: [
      { label: "Understanding hospice", to: "/from-diagnosis-to-goodbye" },
      { label: "Preparing for changes", to: "/from-diagnosis-to-goodbye" },
      { label: "Family communication", to: "/from-diagnosis-to-goodbye" },
      { label: "Caregiver support", to: "/resources" },
      { label: "End-of-life resources", to: "/resources" },
      { label: "From Diagnosis to Goodbye", to: "/from-diagnosis-to-goodbye" },
      { label: "Talk with someone", to: "/request-support" },
    ],
  },

  // -------------------- STRESS --------------------
  stress: {
    title: "Stress & Burnout",
    prompt: "What are you looking for today?",
    options: [
      { label: "Stress management resources", to: "/resources" },
      { label: "Burnout support", to: "/resources" },
      { label: "Caregiver burnout", to: "/resources" },
      { label: "Workplace stress", goto: "workplace" },
      { label: "Self-care resources", to: "/resources" },
      { label: "Talk with someone", to: "/request-support" },
    ],
  },
  workplace: {
    title: "Workplace Stress",
    prompt:
      "For workplace-focused chaplaincy and support, CCCHAPS is a separate organization our founder is connected with. You may also explore Begin Again Living Ministries resources for personal encouragement.",
    options: [
      { label: "Visit CCCHAPS (opens new tab)", href: "https://ccchaps.org" },
      { label: "Self-care resources (BALM)", to: "/resources" },
      { label: "Talk with the ministry", to: "/contact" },
    ],
  },

  // -------------------- PRAYER --------------------
  prayer: {
    title: "Prayer Request",
    prompt:
      "You're welcome to submit a prayer request. Would you like to continue to the prayer request form?",
    options: [
      { label: "Submit Prayer Request", to: "/request-support?type=prayer" },
      { label: "Not right now — return to menu", action: "restart" },
    ],
  },

  // -------------------- BOOKS --------------------
  books: {
    title: "Books & Resources",
    prompt: "What kind of resource are you looking for?",
    options: [
      { label: "From Diagnosis to Goodbye", to: "/from-diagnosis-to-goodbye" },
      { label: "Grief resources", to: "/resources" },
      { label: "Caregiver resources", to: "/resources" },
      { label: "Hospice resources", to: "/resources" },
      { label: "Free downloads", to: "/resources" },
      { label: "View all books", to: "/books" },
      { label: "Sponsor a Book", to: "/sponsor-a-book" },
      { label: "Bulk orders (churches, hospices, ministries)", to: "/bulk-orders" },
    ],
  },

  // -------------------- WORKSHOPS --------------------
  workshops: {
    title: "Workshops & Classes",
    prompt: "Are you looking for something for yourself, a group, or an organization?",
    options: [
      { label: "Individual class", to: "/classes" },
      { label: "Church or ministry group", to: "/classes" },
      { label: "Caregiver group", to: "/classes" },
      { label: "Hospice / community organization", to: "/bulk-orders" },
      { label: "Workshop or speaking request", to: "/contact?topic=speaking" },
      { label: "View available programs", to: "/classes" },
    ],
  },

  // -------------------- ASK CHAPLAIN --------------------
  ask: {
    title: "Ask Chaplain Derrick",
    prompt:
      "Ask Chaplain Derrick offers practical answers, teaching, podcast/media content, and a way to submit questions.",
    options: [
      { label: "Visit Ask Chaplain Derrick", to: "/ask-chaplain-derrick" },
      { label: "Submit a question", to: "/ask-chaplain-derrick#submit" },
      { label: "Listen / watch", to: "/ask-chaplain-derrick#podcast" },
      { label: "Featured book", to: "/ask-chaplain-derrick#book" },
    ],
  },

  // -------------------- CONTACT --------------------
  contact: {
    title: "Contact the Ministry",
    prompt: "How can we help you connect?",
    options: [
      { label: "General contact", to: "/contact" },
      { label: "Speaking request", to: "/contact?topic=speaking" },
      { label: "Workshop request", to: "/contact?topic=workshop" },
      { label: "Prayer request", to: "/request-support?type=prayer" },
      { label: "Book / bulk order question", to: "/bulk-orders" },
      { label: "Licensing question", to: "/contact?topic=licensing" },
    ],
  },

  // -------------------- UNSURE --------------------
  unsure: {
    title: "Not Sure Where to Start",
    prompt: "That's okay. Which statement is closest to what you're dealing with?",
    options: [
      { label: "I'm grieving", goto: "grief" },
      { label: "I'm caring for someone", goto: "caregiving" },
      { label: "Someone I love is seriously ill", goto: "hospice" },
      { label: "I feel overwhelmed or burned out", goto: "stress" },
      { label: "I need spiritual encouragement", to: "/ask-chaplain-derrick" },
      { label: "I just want to explore resources", to: "/resources" },
      { label: "I want to talk with someone", to: "/request-support" },
    ],
  },
};

/**
 * Free-text keyword → node mapping. Ordered: first match wins.
 * Crisis words are handled separately in crisis.js.
 */
export const KEYWORD_ROUTES = [
  { keywords: ["grief", "grieving", "loss", "died", "death", "passed away", "funeral", "bereaved", "widow", "widower"], goto: "grief" },
  { keywords: ["hospice", "terminal", "dying", "end of life", "end-of-life", "diagnosis", "cancer", "palliative"], goto: "hospice" },
  { keywords: ["caregiver", "caregiving", "caring for", "taking care of", "parent needs", "mom is sick", "dad is sick"], goto: "caregiving" },
  { keywords: ["stress", "stressed", "burnout", "burned out", "overwhelmed", "exhausted", "tired", "anxious"], goto: "stress" },
  { keywords: ["pray", "prayer", "praying"], goto: "prayer" },
  { keywords: ["book", "books", "workbook", "guide", "pdf", "download", "resource"], goto: "books" },
  { keywords: ["class", "classes", "workshop", "workshops", "chapel", "zoom"], goto: "workshops" },
  { keywords: ["chaplain", "podcast", "ask derrick", "ask chaplain"], goto: "ask" },
  { keywords: ["contact", "email", "call", "speak", "speaker", "speaking", "book him", "bulk order"], goto: "contact" },
];

export function routeFreeText(raw) {
  const text = (raw || "").toLowerCase();
  if (!text.trim()) return null;
  for (const { keywords, goto } of KEYWORD_ROUTES) {
    if (keywords.some((kw) => text.includes(kw))) return goto;
  }
  return "unsure";
}
